<template>
  <v-card class="mb-3" variant="outlined">
    <v-card-text>
      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
        <div class="text-subtitle-2 ncrm-text-strong">
          {{ $t("ncrm.onboarding.templates.create.stages.stageLabel", { index: stageIndex + 1 }) }}
        </div>
        <v-btn
          size="x-small"
          variant="text"
          color="error"
          :disabled="!canRemove"
          @click="$emit('remove-stage', stage.local_id)"
        >
          <v-icon start>mdi-delete-outline</v-icon>
          {{ $t("app.actions.delete") }}
        </v-btn>
      </div>

      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="stage.name"
            :label="$t('ncrm.onboarding.templates.create.stages.fields.name')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="stage.owner_user_id"
            :items="ownerItems"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            :loading="ownerLoading"
            :label="$t('ncrm.onboarding.templates.create.stages.fields.ownerUser')"
            clearable
            @update:search="$emit('owner-search', $event)"
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-text-field
            v-model.number="stage.sort_order"
            type="number"
            min="1"
            :label="$t('ncrm.onboarding.templates.create.stages.fields.sortOrder')"
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-switch
            v-model="stage.is_terminal"
            color="primary"
            inset
            :label="$t('ncrm.onboarding.templates.create.stages.fields.isTerminal')"
          />
        </v-col>
      </v-row>

      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
        <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.templates.create.tasks.title") }}</div>
        <v-btn size="small" variant="text" color="primary" @click="$emit('add-task', stage.local_id)">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.onboarding.templates.create.tasks.add") }}
        </v-btn>
      </div>

      <v-alert v-if="!stage.tasks.length" type="info" density="comfortable" class="mb-2">
        {{ $t("ncrm.onboarding.templates.create.tasks.empty") }}
      </v-alert>

      <v-row
        v-for="(task, taskIndex) in stage.tasks"
        :key="`stage-${stage.local_id}-task-${task.local_id}`"
        dense
        class="ncrm-onboarding-templates__task-row"
      >
        <v-col cols="12" md="4">
          <v-text-field
            v-model="task.title"
            :label="$t('ncrm.onboarding.templates.create.tasks.fields.title')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="task.description"
            :label="$t('ncrm.onboarding.templates.create.tasks.fields.description')"
          />
        </v-col>
        <v-col cols="6" md="2">
          <v-text-field
            v-model.number="task.due_offset_days"
            type="number"
            :label="$t('ncrm.onboarding.templates.create.tasks.fields.dueOffset')"
          />
        </v-col>
        <v-col cols="6" md="1">
          <v-text-field
            v-model.number="task.sort_order"
            type="number"
            min="1"
            :label="$t('ncrm.onboarding.templates.create.tasks.fields.sortOrder')"
          />
        </v-col>
        <v-col cols="12" md="1" class="d-flex align-center justify-end pb-md-5">
          <v-btn size="x-small" variant="text" color="error" @click="$emit('remove-task', stage.local_id, task.local_id)">
            <v-icon size="16">mdi-delete-outline</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" class="pt-0">
          <v-switch
            v-model="task.is_required"
            color="primary"
            inset
            :label="$t('ncrm.onboarding.templates.create.tasks.fields.isRequired')"
          />
        </v-col>
        <v-col v-if="taskIndex < stage.tasks.length - 1" cols="12" class="pt-0">
          <v-divider />
        </v-col>
      </v-row>

      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2 mt-3">
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.onboarding.templates.create.fieldsEditor.stageTitle") }}
        </div>
        <v-btn size="small" variant="text" color="primary" @click="$emit('add-field', stage.local_id)">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("ncrm.onboarding.templates.create.fieldsEditor.add") }}
        </v-btn>
      </div>

      <v-alert
        v-if="!stage.fields.length"
        type="info"
        density="comfortable"
        class="mb-2"
      >
        {{ $t("ncrm.onboarding.templates.create.fieldsEditor.emptyStage") }}
      </v-alert>

      <NcrmOnboardingTemplateFieldRows
        :fields="stage.fields"
        :field-type-options="fieldTypeOptions"
        :select-menu-props="selectMenuProps"
        :item-key-prefix="`stage-${stage.local_id}-field`"
        show-dividers
        @remove="$emit('remove-field', stage.local_id, $event)"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import NcrmOnboardingTemplateFieldRows from "./NcrmOnboardingTemplateFieldRows.vue";

export default {
  name: "NcrmOnboardingTemplateStageEditor",
  components: {
    NcrmOnboardingTemplateFieldRows,
  },
  props: {
    stage: {
      type: Object,
      required: true,
    },
    stageIndex: {
      type: Number,
      default: 0,
    },
    canRemove: {
      type: Boolean,
      default: true,
    },
    fieldTypeOptions: {
      type: Array,
      default: () => [],
    },
    selectMenuProps: {
      type: Object,
      default: () => ({}),
    },
    ownerItems: {
      type: Array,
      default: () => [],
    },
    ownerLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["remove-stage", "add-task", "remove-task", "add-field", "remove-field", "owner-search"],
};
</script>
